import { OpenStreetMapProvider } from "leaflet-geosearch";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { handleAPIAddressToGeocode } from "../API/handleAddressAPI";
import { clearLocateDenied, markLocateDenied } from "./locatePermission";

interface CombinedAddressParams {
  city: string;
  area: string;
  road: string;
  streetNumber?: string;
  floor?: string;
  room?: string;
}

//@ 組合地址
export const combinedAddress = ({ city, area, road, streetNumber, floor, room }: CombinedAddressParams): string => {
  if (!city) return "";
  if (!area) return city;
  if (!road) return city + area;
  const parts = [
    city,
    area,
    road,
    streetNumber ? (streetNumber.endsWith("號") ? streetNumber : `${streetNumber}號`) : "",
    floor ? (floor.endsWith("樓") ? floor : `${floor}樓`) : "",
    room ? (room.endsWith("室") ? room : `${room}室`) : "",
  ];

  return parts.filter(Boolean).join("");
};

//@ 取得目前定位點
interface LocationResult {
  state: "success" | "error";
  lat: number | string;
  lng: number | string;
  errorMsg?: string;
}

/**
 * 呼叫端等不下去的上限（毫秒）。
 *
 * getCurrentPosition 的 timeout 選項救不了「使用者盯著系統權限窗不按」：依規格那個倒數是
 * 從權限拿到之後才開始算的，提示期間不計時，而使用者不回應時 success/error 兩個 callback
 * 都不會來 —— Promise 就永遠不 resolve，呼叫端的 loading toast 與 spinner 會一直掛著。
 * 這層 watchdog 專門堵這條路徑；設得比 timeout(10s) 寬鬆，是留給猶豫幾秒才按「允許」的人。
 */
const LOCATE_WATCHDOG_MS = 20000;

//@ 取得使用者目前定位
//API - Web API Navigator
export const getCurrentLocation = (): Promise<LocationResult> => {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve({ state: "error", errorMsg: "您的瀏覽器不支援定位功能", lat: "", lng: "" });
      return;
    }

    // 兩個 callback 與 watchdog 三者搶著結束，只有最先到的算數
    let settled = false;
    const finish = (result: LocationResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      resolve(result);
    };
    const watchdog = setTimeout(
      () => finish({ state: "error", errorMsg: "定位逾時", lat: "", lng: "" }),
      LOCATE_WATCHDOG_MS,
    );

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        clearLocateDenied(); // 拿得到位置 → 清掉拒絕紀錄，下次恢復直接叫系統窗
        finish({ state: "success", lat: Number(latitude).toFixed(4), lng: Number(longitude).toFixed(4) });
      },
      (error) => {
        // 只有「明確拒絕」才記旗標：訊號差導致的逾時／取不到若也記成拒絕，下次會多彈一次
        // 我們的說明卡，而那時系統窗其實還會跳 —— 又變成使用者要點兩次。
        if (error.code === 1) markLocateDenied();
        const errorMsg =
          {
            1: "您拒絕了定位權限請求",
            2: "無法取得位置資訊",
            3: "定位逾時",
          }[error.code as 1 | 2 | 3] || "發生未知錯誤";

        finish({ state: "error", errorMsg, lat: "", lng: "" });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
};

//@ 地址轉換為座標（無結果或搜尋失敗時回傳 state: "error"）
//  來源順序：自家 address API（/address/repair，Polstar → Google → TomTom → HERE）→ OSM Nominatim。
//  OSM 對台灣門牌覆蓋率差，且其使用條款不允許當產品後端，只留作最後保險。
const provider = new OpenStreetMapProvider();
export const addressToGeocode = async (address: string) => {
  //-- 非 React 環境，直接從 zustand store 取 token（此函式被 8 處呼叫，多數不在元件內）
  const lineAccessToken = useLineAuth.getState().lineAccessToken;
  if (lineAccessToken) {
    const apiGeocode = await handleAPIAddressToGeocode(address, lineAccessToken);
    if (apiGeocode) {
      return { state: "success", lat: String(apiGeocode.lat), lng: String(apiGeocode.lng) };
    }
  }

  try {
    const results = await provider.search({ query: address });
    if (results && results.length > 0) {
      return { state: "success", lat: Number(results[0].y).toFixed(4), lng: Number(results[0].x).toFixed(4) };
    }
    return { state: "error", lat: "", lng: "", errorMsg: "找不到此地址的座標" };
  } catch (error) {
    console.error("Geosearch 搜尋失敗:", error);
    return { state: "error", lat: "", lng: "", errorMsg: "Geosearch 搜尋失敗" };
  }
};

//@ 座標反查地址（OpenStreetMap Nominatim，免授權；供無 LINE token 的情境使用）
export interface ReverseAddress {
  city: string;
  area: string;
  road: string;
  streetNumber: string;
}
export const geocodeToAddress = async (lat: number, lng: number): Promise<ReverseAddress | null> => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=zh-TW`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const data = await res.json();
    const a = data?.address || {};
    // 臺→台 與台灣行政層級對應
    const norm = (s?: string) => (s || "").replace(/臺/g, "台");
    return {
      city: norm(a.city || a.county || a.state),
      area: norm(a.suburb || a.city_district || a.town || a.district || a.village),
      road: norm(a.road || a.pedestrian || a.neighbourhood),
      streetNumber: norm(a.house_number),
    };
  } catch (error) {
    console.error("Nominatim reverse 失敗:", error);
    return null;
  }
};

//@ 計算兩點之間的距離（Haversine，單位：公尺）
export const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

//@ 檢查縣市&行政區是否匹配
interface District {
  name: string;
}
export const isAreaInCityValid = (city: string, area: string): boolean => {
  const taiwanData = require("../../../data/taiwanDistricts.json");
  const cityDistricts = taiwanData[city as keyof typeof taiwanData];
  if (!cityDistricts) return false;
  return cityDistricts.some((district: District) => district?.name === area);
};
